import { FormSpacing, StyledDateInput } from "@src/common/styled-components";
import {
  GenderIdentityOptions,
  LanguagesOptions,
  LegalSexOptions,
  MAX_PHONE_NUMBER_LENGTH,
  ONE_DAY_IN_MS,
  PhoneTypeOptions,
  PreferredContactMethodOptions,
  PreferredPronounsOptions,
  YesNoOptions,
} from "@src/constants";
import { dayjs } from "@uhg-abyss/web/tools/dayjs";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Checkbox } from "@uhg-abyss/web/ui/Checkbox";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import type { ChangeEvent } from "react";

const FormRow = styled("div", {
  display: "flex",
  gap: "$core.spacing.300",
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
      },
      false: {
        flexDirection: "row",
      },
    },
  },
});

export interface ParentGuardianFormData {
  isPolicyHolder: "yes" | "no" | "";
  firstName: string;
  lastName: string;
  dob: string;
  legalSex: string;
  genderIdentity: string;
  preferredPronouns: string;
  preferredLanguage: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  phone: string;
  phoneType: string;
  preferredContactMethod: string;
  voicemailPreference: string;
}

export interface ParentGuardianFormErrors {
  isPolicyHolder?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  legalSex?: string;
  genderIdentity?: string;
  preferredPronouns?: string;
  preferredLanguage?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  preferredContactMethod?: string;
  voicemailPreference?: string;
}

interface ParentGuardianSectionProps {
  isMobile: boolean;
  formData: ParentGuardianFormData;
  formErrors: ParentGuardianFormErrors;
  onFieldChange: (field: keyof ParentGuardianFormData, value: string) => void;
  sameAsPatientAddress: boolean;
  onSameAsPatientAddressChange: (checked: boolean) => void;
  isPatientAddressFilled: boolean;
  isPolicyHolderDisabled?: boolean;
  isPolicyHolderHidden?: boolean;
  canvasIsInSalesforce?: boolean;
}

export const ParentGuardianSection = ({
  isMobile,
  formData,
  formErrors,
  onFieldChange,
  sameAsPatientAddress,
  onSameAsPatientAddressChange,
  isPatientAddressFilled,
  isPolicyHolderDisabled = false,
  isPolicyHolderHidden = false,
  canvasIsInSalesforce = false,
}: ParentGuardianSectionProps) => {
  return (
    <FormSpacing>
      <Heading size="xs">Parent/Guardian</Heading>
      {!isPolicyHolderHidden && (
        <SelectInput
          data-testid="test-id-parent-guardian-policy-holder-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Is parent/guardian the policy holder?"
          value={formData.isPolicyHolder}
          onChange={(value) => {
            return onFieldChange("isPolicyHolder", String(value));
          }}
          isRequired={!isPolicyHolderDisabled}
          isDisabled={isPolicyHolderDisabled}
          errorMessage={formErrors.isPolicyHolder}
          options={YesNoOptions}
          aria-label="Is parent or guardian the policy holder?"
        />
      )}

      <FormRow isMobile={isMobile}>
        <TextInput
          data-testid="test-id-parent-guardian-first-name-input"
          label="First name"
          value={formData.firstName}
          isRequired
          onChange={(e) => {
            return onFieldChange("firstName", e.target.value);
          }}
          errorMessage={formErrors.firstName}
          css={{ flex: 1 }}
          aria-label="Enter first name of parent or guardian"
        />
        <TextInput
          data-testid="test-id-parent-guardian-last-name-input"
          label="Last name"
          value={formData.lastName}
          isRequired
          onChange={(e) => {
            return onFieldChange("lastName", e.target.value);
          }}
          errorMessage={formErrors.lastName}
          css={{ flex: 1 }}
          aria-label="Enter last name of parent or guardian"
        />
      </FormRow>

      <FormRow isMobile={isMobile}>
        <StyledDateInput
          data-testid="test-id-parent-guardian-dob-input"
          isRequired
          label="Date of birth"
          value={formData.dob || ""}
          maxDate={dayjs(new Date(Date.now() - ONE_DAY_IN_MS))}
          onChange={(e) => {
            const dobValue =
              typeof e === "string" ? e : (e as ChangeEvent<HTMLInputElement>)?.target?.value;
            onFieldChange("dob", dobValue || "");
          }}
          errorMessage={formErrors.dob}
          aria-label="Enter date of birth of parent or guardian"
        />
        <SelectInput
          data-testid="test-id-parent-guardian-legal-sex-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Legal sex"
          value={formData.legalSex}
          onChange={(value) => {
            return onFieldChange("legalSex", String(value));
          }}
          isRequired
          errorMessage={formErrors.legalSex}
          options={LegalSexOptions}
          css={{ flex: 1 }}
          aria-label="Select legal sex of parent or guardian"
        />
      </FormRow>

      <FormRow isMobile={isMobile}>
        <SelectInput
          data-testid="test-id-parent-guardian-gender-identity-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Gender identity (optional)"
          value={formData.genderIdentity}
          onChange={(value) => {
            return onFieldChange("genderIdentity", String(value));
          }}
          errorMessage={formErrors.genderIdentity}
          aria-label="Select gender identity of parent or guardian"
          options={GenderIdentityOptions}
          css={{ flex: 1 }}
        />
        <SelectInput
          data-testid="test-id-parent-guardian-pronouns-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Preferred pronouns (optional)"
          value={formData.preferredPronouns}
          onChange={(value) => {
            return onFieldChange("preferredPronouns", String(value));
          }}
          errorMessage={formErrors.preferredPronouns}
          options={PreferredPronounsOptions}
          css={{ flex: 1 }}
          aria-label="Select preferred pronouns of parent or guardian"
        />
      </FormRow>

      <SelectInput
        data-testid="test-id-parent-guardian-language-select"
        enableOutsideScroll={canvasIsInSalesforce}
        label="Preferred language (optional)"
        value={formData.preferredLanguage}
        onChange={(value) => {
          return onFieldChange("preferredLanguage", String(value));
        }}
        errorMessage={formErrors.preferredLanguage}
        options={[
          ...LanguagesOptions.map((language) => {
            return { value: language, label: language };
          }),
        ]}
        aria-label="Select preferred language of parent or guardian"
      />

      <Checkbox
        data-testid="test-id-parent-guardian-same-address-checkbox"
        label="Same as Patient Address"
        checked={sameAsPatientAddress}
        onChange={(e) => {
          return onSameAsPatientAddressChange((e as ChangeEvent<HTMLInputElement>).target.checked);
        }}
        id="guardian-same-as-patient-address"
        data-id="guardian-same-as-patient-address"
        data-obc-label="Same as Patient Address"
        isDisabled={!isPatientAddressFilled}
        aria-disabled={!isPatientAddressFilled}
        aria-label="Same as patient address for parent or guardian address"
      />

      <TextInput
        data-testid="test-id-parent-guardian-street-address-input"
        label="Street address"
        value={formData.streetAddress1}
        isRequired
        onChange={(e) => {
          return onFieldChange("streetAddress1", e.target.value);
        }}
        errorMessage={formErrors.streetAddress1}
        aria-label="Enter street address of parent or guardian"
      />

      <TextInput
        data-testid="test-id-parent-guardian-street-address-2-input"
        label="Street address 2 (optional)"
        value={formData.streetAddress2}
        onChange={(e) => {
          return onFieldChange("streetAddress2", e.target.value);
        }}
        errorMessage={formErrors.streetAddress2}
        aria-label="Enter street address 2 of parent or guardian"
      />

      <FormRow isMobile={isMobile}>
        <TextInput
          data-testid="test-id-parent-guardian-city-input"
          label="City"
          value={formData.city}
          isRequired
          onChange={(e) => {
            return onFieldChange("city", e.target.value);
          }}
          errorMessage={formErrors.city}
          css={{ flex: 1 }}
          aria-label="Enter city of parent or guardian"
        />
        <TextInput
          data-testid="test-id-parent-guardian-state-input"
          label="State"
          value={formData.state}
          isRequired
          onChange={(e) => {
            return onFieldChange("state", e.target.value);
          }}
          errorMessage={formErrors.state}
          css={{ flex: 1 }}
          aria-label="Enter state of parent or guardian"
        />
        <TextInput
          data-testid="test-id-parent-guardian-zip-input"
          label="Zip code"
          value={formData.zipCode}
          isRequired
          onChange={(e) => {
            return onFieldChange("zipCode", e.target.value);
          }}
          errorMessage={formErrors.zipCode}
          css={{ flex: 1 }}
          aria-label="Enter zip code of parent or guardian"
        />
      </FormRow>

      <TextInput
        data-testid="test-id-parent-guardian-email-input"
        label="Email"
        value={formData.email}
        isRequired
        onChange={(e) => {
          return onFieldChange("email", e.target.value);
        }}
        errorMessage={formErrors.email}
        placeholder="name@email.com"
        aria-label="Enter email of parent or guardian"
      />

      <FormRow isMobile={isMobile}>
        <TextInput
          data-testid="test-id-parent-guardian-phone-input"
          label="Phone"
          value={formData.phone}
          isRequired
          onChange={(e) => {
            return onFieldChange("phone", e.target.value);
          }}
          placeholder="(223) 456-7890"
          errorMessage={formErrors.phone}
          maxLength={MAX_PHONE_NUMBER_LENGTH}
          css={{ flex: 1 }}
          aria-label="Enter phone number of parent or guardian"
        />
        <SelectInput
          data-testid="test-id-parent-guardian-phone-type-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Phone type"
          value={formData.phoneType}
          onChange={(value) => {
            return onFieldChange("phoneType", String(value));
          }}
          isRequired
          errorMessage={formErrors.phoneType}
          options={PhoneTypeOptions}
          css={{ flex: 1 }}
          aria-label="Select phone type of parent or guardian"
        />
      </FormRow>

      <FormRow isMobile={isMobile}>
        <SelectInput
          data-testid="test-id-parent-guardian-contact-method-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Preferred method of contact"
          value={formData.preferredContactMethod}
          onChange={(value) => {
            return onFieldChange("preferredContactMethod", String(value));
          }}
          isRequired
          errorMessage={formErrors.preferredContactMethod}
          options={PreferredContactMethodOptions}
          css={{ flex: 1 }}
          aria-label="Select preferred method of contact for parent or guardian"
        />
        <SelectInput
          data-testid="test-id-parent-guardian-voicemail-select"
          enableOutsideScroll={canvasIsInSalesforce}
          label="Okay to leave voicemails?"
          value={formData.voicemailPreference}
          onChange={(value) => {
            return onFieldChange("voicemailPreference", String(value));
          }}
          isRequired
          errorMessage={formErrors.voicemailPreference}
          options={YesNoOptions}
          css={{ flex: 1 }}
          aria-label="Select voicemail preference for parent or guardian"
        />
      </FormRow>
    </FormSpacing>
  );
};
